// Иконка вкладки, по которой летает самолёт: набор высоты, полка вправо,
// мёртвая петля — и снова вверх. Через центр знака самолёт проходит близко к
// зрителю и крупным, к краям и на верхушке петли уходит вдаль и мельчает.
//
// Браузеры не анимируют ни SVG-фавиконку, ни GIF (Chrome и Safari показывают
// первый кадр), поэтому единственный рабочий способ — рисовать кадры самим и
// подменять href у <link rel="icon">.
//
// Знак разложен на два слоя: плитка с полосой стоит на месте (favicon-base.svg),
// летает только самолёт (favicon-plane.svg). Цельный favicon.svg остаётся в
// index.html: его видно, пока кадры считаются, и он же остаётся, если движение
// выключено.
//
// Кадры считаются один раз при старте и дальше только перебираются: canvas +
// toDataURL семнадцать раз в секунду — это заметный расход батареи ради иконки
// размером с ноготь.
//
// Движение выключается, если система просит уменьшить анимацию, и замирает,
// пока вкладка в фоне: там его всё равно никто не видит.

export interface FlightFaviconOptions {
  /** Неподвижный слой: рисуется во весь кадр как есть. */
  base?: string;
  /** Летающий слой: самолёт носом вправо, по центру своего кадра. */
  plane?: string;
  /** Те же два слоя для системной тёмной схемы. Пара выбирается здесь, а не
      медиазапросом внутри SVG: медиазапросы в SVG, нарисованном на canvas
      через Image, движки считают по-разному, и кадры разъезжались бы со
      статичной иконкой. */
  baseDark?: string;
  planeDark?: string;
  /** Сторона кадра в пикселях. 64 хватает всем плотностям вкладки. */
  size?: number;
  /** Кадров на круг. 48 при периоде ниже — это ~17 кадров в секунду. */
  frames?: number;
  /** Длительность полного круга. */
  periodMs?: number;
  /** Сторона кадра самолёта у центра знака — самый близкий к зрителю размер. */
  planeSpanNear?: number;
  /** Сторона кадра самолёта на удалении: верхушка петли, края круга. */
  planeSpanFar?: number;
}

const DEFAULTS = {
  base: '/favicon-base.svg',
  plane: '/favicon-plane.svg',
  baseDark: '/favicon-base-dark.svg',
  planeDark: '/favicon-plane-dark.svg',
  size: 64,
  frames: 48,
  periodMs: 2800,
  planeSpanNear: 15,
  planeSpanFar: 6.5,
} satisfies Required<FlightFaviconOptions>;

// Точка, к которой самолёт «подлетает» ближе всего, и расстояние, на котором
// он уже считается далёким. Размер меняется плавно, потому что расстояние до
// центра вдоль гладкой траектории тоже меняется плавно.
const NEAR_POINT: Point = { x: 16, y: 16 };
const FAR_DISTANCE = 10.5;

interface Point {
  x: number;
  y: number;
}

/** Прямая пролетается насквозь, дуга — от одного курса к другому. */
type Leg =
  | { kind: 'line'; from: Point; to: Point }
  | { kind: 'arc'; center: Point; radius: number; fromDeg: number; toDeg: number };

const RAD = Math.PI / 180;

// Круг полёта в координатах знака (32×32), ось Y вниз. Замкнут: последняя дуга
// приводит самолёт в точку и курс первой прямой, иначе на стыке кадров был бы
// скачок. Габариты подобраны так, чтобы законцовка крыла не вылезала за плитку
// в верхней точке петли и не задевала полосу на нижней прямой.
const CIRCUIT: Leg[] = [
  // Набор высоты вдоль левого края — как раз оттуда, где кончается полоса.
  { kind: 'line', from: { x: 7.5, y: 19.5 }, to: { x: 7.5, y: 16.9 } },
  // Разворот из набора в горизонт.
  { kind: 'arc', center: { x: 10, y: 16.9 }, radius: 2.5, fromDeg: 180, toDeg: 270 },
  // Полка вправо — разгон перед петлёй.
  { kind: 'line', from: { x: 10, y: 14.4 }, to: { x: 17, y: 14.4 } },
  // Мёртвая петля: вверх, через спину, вниз и снова в горизонт. Радиус больше
  // длины самолёта — иначе петля читается не как петля, а как вращение волчком.
  { kind: 'arc', center: { x: 17, y: 10.2 }, radius: 4.2, fromDeg: 90, toDeg: -270 },
  // Выход из петли тем же курсом.
  { kind: 'line', from: { x: 17, y: 14.4 }, to: { x: 22, y: 14.4 } },
  // Возврат по кругу: вниз по правому краю, влево понизу и снова в набор.
  { kind: 'arc', center: { x: 22, y: 16.9 }, radius: 2.5, fromDeg: 270, toDeg: 360 },
  { kind: 'line', from: { x: 24.5, y: 16.9 }, to: { x: 24.5, y: 19.5 } },
  { kind: 'arc', center: { x: 22, y: 19.5 }, radius: 2.5, fromDeg: 0, toDeg: 90 },
  { kind: 'line', from: { x: 22, y: 22 }, to: { x: 10, y: 22 } },
  { kind: 'arc', center: { x: 10, y: 19.5 }, radius: 2.5, fromDeg: 90, toDeg: 180 },
];

interface Pose extends Point {
  /** Курс в радианах: 0 — вправо, как нарисован самолёт. */
  angle: number;
}

function legLength(leg: Leg): number {
  if (leg.kind === 'line') {
    return Math.hypot(leg.to.x - leg.from.x, leg.to.y - leg.from.y);
  }
  return leg.radius * Math.abs(leg.toDeg - leg.fromDeg) * RAD;
}

function poseAt(leg: Leg, u: number): Pose {
  if (leg.kind === 'line') {
    const dx = leg.to.x - leg.from.x;
    const dy = leg.to.y - leg.from.y;
    return {
      x: leg.from.x + dx * u,
      y: leg.from.y + dy * u,
      angle: Math.atan2(dy, dx),
    };
  }

  const deg = leg.fromDeg + (leg.toDeg - leg.fromDeg) * u;
  const a = deg * RAD;
  // Касательная к дуге; знак обхода задаёт, каким боком самолёт идёт по кругу.
  const spin = Math.sign(leg.toDeg - leg.fromDeg) || 1;
  return {
    x: leg.center.x + leg.radius * Math.cos(a),
    y: leg.center.y + leg.radius * Math.sin(a),
    angle: Math.atan2(spin * Math.cos(a), -spin * Math.sin(a)),
  };
}

/** Точка круга по пройденному пути: скорость постоянная, без рывков на стыках. */
function poseAtDistance(lengths: number[], total: number, distance: number): Pose {
  let left = ((distance % total) + total) % total;
  for (let i = 0; i < CIRCUIT.length; i += 1) {
    if (left <= lengths[i] || i === CIRCUIT.length - 1) {
      return poseAt(CIRCUIT[i], lengths[i] === 0 ? 0 : Math.min(left / lengths[i], 1));
    }
    left -= lengths[i];
  }
  return poseAt(CIRCUIT[0], 0);
}

function findIconLink(): HTMLLinkElement | null {
  return document.querySelector<HTMLLinkElement>('link[rel~="icon"]');
}

function loadImage(href: string): Promise<HTMLImageElement> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.decoding = 'async';
    img.onload = () => resolve(img);
    img.onerror = () => reject(new Error(`иконка не загрузилась: ${href}`));
    img.src = href;
  });
}

/** Размер самолёта по удалению от центра знака: у центра ближний, дальше — дальний. */
function spanAt(pose: Pose, near: number, far: number): number {
  const distance = Math.hypot(pose.x - NEAR_POINT.x, pose.y - NEAR_POINT.y);
  const t = Math.min(distance / FAR_DISTANCE, 1);
  return near + (far - near) * t;
}

function renderFrames(
  base: HTMLImageElement,
  plane: HTMLImageElement,
  { size, frames, planeSpanNear, planeSpanFar }: Required<FlightFaviconOptions>,
): string[] {
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  // jsdom и старые движки бросают на getContext вместо того, чтобы вернуть
  // null: без кадров просто остаётся статичная иконка.
  let ctx: CanvasRenderingContext2D | null = null;
  try {
    ctx = canvas.getContext('2d');
  } catch {
    return [];
  }
  if (!ctx) return [];

  const lengths = CIRCUIT.map(legLength);
  const total = lengths.reduce((sum, l) => sum + l, 0);
  const scale = size / 32; // знак нарисован в 32×32
  const out: string[] = [];

  for (let i = 0; i < frames; i += 1) {
    const pose = poseAtDistance(lengths, total, (total * i) / frames);
    const span = spanAt(pose, planeSpanNear, planeSpanFar) * scale;
    ctx.clearRect(0, 0, size, size);
    ctx.drawImage(base, 0, 0, size, size);
    ctx.save();
    ctx.translate(pose.x * scale, pose.y * scale);
    ctx.rotate(pose.angle);
    ctx.drawImage(plane, -span / 2, -span / 2, span, span);
    ctx.restore();
    out.push(canvas.toDataURL('image/png'));
  }

  return out;
}

/**
 * Запускает полёт и возвращает функцию остановки, которая возвращает
 * исходную иконку на место.
 */
export function startFlightFavicon(options: FlightFaviconOptions = {}): () => void {
  const settings = { ...DEFAULTS, ...options };

  const link = findIconLink();
  if (!link || typeof document.createElement('canvas').getContext !== 'function') {
    return () => {};
  }

  const staticHref = link.href;
  const staticType = link.type;
  const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)') ?? null;
  const darkScheme = window.matchMedia?.('(prefers-color-scheme: dark)') ?? null;

  let stopped = false;
  let timer: ReturnType<typeof setInterval> | null = null;
  let sequence: string[] = [];
  let frame = 0;

  function showStatic(): void {
    link!.type = staticType;
    link!.href = staticHref;
  }

  function pause(): void {
    if (timer !== null) {
      clearInterval(timer);
      timer = null;
    }
    showStatic();
  }

  function play(): void {
    if (stopped || timer !== null || sequence.length === 0) return;
    if (reduced?.matches || document.hidden) return;
    timer = setInterval(
      () => {
        frame = (frame + 1) % sequence.length;
        link!.type = 'image/png';
        link!.href = sequence[frame];
      },
      Math.round(settings.periodMs / settings.frames),
    );
  }

  function sync(): void {
    if (reduced?.matches || document.hidden) pause();
    else play();
  }

  /** Кадры считаются под текущую схему; при её смене — пересчитываются. */
  function loadLayers(): void {
    const dark = darkScheme?.matches ?? false;
    Promise.all([
      loadImage(dark ? settings.baseDark : settings.base),
      loadImage(dark ? settings.planeDark : settings.plane),
    ])
      .then(([base, plane]) => {
        if (stopped) return;
        sequence = renderFrames(base, plane, settings);
        frame = 0;
        sync();
      })
      .catch(() => {
        // Слои не загрузились — остаётся статичная иконка из index.html.
      });
  }

  // Пока новые кадры считаются, видна статичная иконка — она сменила схему
  // сама: медиазапрос внутри favicon.svg браузер пересчитывает без нас.
  function onSchemeChange(): void {
    pause();
    sequence = [];
    loadLayers();
  }

  loadLayers();

  document.addEventListener('visibilitychange', sync);
  reduced?.addEventListener('change', sync);
  darkScheme?.addEventListener('change', onSchemeChange);

  return () => {
    stopped = true;
    pause();
    document.removeEventListener('visibilitychange', sync);
    reduced?.removeEventListener('change', sync);
    darkScheme?.removeEventListener('change', onSchemeChange);
  };
}
